import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  addDoc,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore'
import { firestore } from '../config/firebase'
import { UserProfile, AdminSettings, Subscription, Payment, SubscriptionPlan, PromoCode } from '../types/user'
import { AnalyticsService } from './analytics'

// Collection names
const COLLECTIONS = {
  USERS: 'users',
  ADMIN_SETTINGS: 'adminSettings',
  SUBSCRIPTIONS: 'subscriptions',
  PAYMENTS: 'payments',
  PRICING_PLANS: 'pricingPlans',
  MEMBERS: 'members',
  PROMO_CODES: 'promoCodes'
} as const

// User Management
export const userService = {
  // Get user profile
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(firestore, COLLECTIONS.USERS, uid))
      if (userDoc.exists()) {
        return userDoc.data() as UserProfile
      }
      return null
    } catch (error) {
      console.error('Error getting user profile:', error)
      throw error
    }
  },

  // Create or update user profile
  async createUserProfile(uid: string, userData: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(firestore, COLLECTIONS.USERS, uid)
      await setDoc(userRef, {
        ...userData,
        uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error creating user profile:', error)
      throw error
    }
  },

  // Update user profile
  async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(firestore, COLLECTIONS.USERS, uid)
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating user profile:', error)
      throw error
    }
  },

  // Get all users (for admin)
  async getAllUsers(): Promise<UserProfile[]> {
    try {
      const querySnapshot = await getDocs(collection(firestore, COLLECTIONS.USERS))
      return querySnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as UserProfile[]
    } catch (error) {
      console.error('Error getting all users:', error)
      throw error
    }
  }
}

// Admin Settings Management
export const adminService = {
  // Get admin settings
  async getAdminSettings(): Promise<AdminSettings | null> {
    try {
      const settingsDoc = await getDoc(doc(firestore, COLLECTIONS.ADMIN_SETTINGS, 'main'))
      if (settingsDoc.exists()) {
        return settingsDoc.data() as AdminSettings
      }
      return null
    } catch (error) {
      console.error('Error getting admin settings:', error)
      throw error
    }
  },

  // Initialize admin settings
  async initializeAdminSettings(): Promise<void> {
    try {
      const settingsRef = doc(firestore, COLLECTIONS.ADMIN_SETTINGS, 'main')
      const defaultSettings: AdminSettings = {
        paywallEnabled: true,
        vatPercentage: 15,
        maxCourts: 6,
        trialDurationDays: 60,
        currency: 'ZAR',
        updatedAt: serverTimestamp() as any
      }
      
      await setDoc(settingsRef, defaultSettings)
    } catch (error) {
      console.error('Error initializing admin settings:', error)
      throw error
    }
  },

  // Update admin settings
  async updateAdminSettings(updates: Partial<AdminSettings>): Promise<void> {
    try {
      const settingsRef = doc(firestore, COLLECTIONS.ADMIN_SETTINGS, 'main')
      await updateDoc(settingsRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating admin settings:', error)
      throw error
    }
  },

  // Get all users (for admin)
  async getAllUsers(): Promise<UserProfile[]> {
    try {
      const querySnapshot = await getDocs(collection(firestore, COLLECTIONS.USERS))
      return querySnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as UserProfile[]
    } catch (error) {
      console.error('Error getting all users:', error)
      throw error
    }
  }
}

// Subscription Management
export const subscriptionService = {
  // Get user subscription
  async getUserSubscription(userId: string): Promise<Subscription | null> {
    try {
      const subscriptionsQuery = query(
        collection(firestore, COLLECTIONS.SUBSCRIPTIONS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      )
      
      const querySnapshot = await getDocs(subscriptionsQuery)
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data() as Subscription
      }
      return null
    } catch (error) {
      console.error('Error getting user subscription:', error)
      throw error
    }
  },

  // Create subscription
  async createSubscription(subscriptionData: Omit<Subscription, 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const subscriptionRef = await addDoc(collection(firestore, COLLECTIONS.SUBSCRIPTIONS), {
        ...subscriptionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return subscriptionRef.id
    } catch (error) {
      console.error('Error creating subscription:', error)
      throw error
    }
  },

  // Update subscription
  async updateSubscription(subscriptionId: string, updates: Partial<Subscription>): Promise<void> {
    try {
      const subscriptionRef = doc(firestore, COLLECTIONS.SUBSCRIPTIONS, subscriptionId)
      await updateDoc(subscriptionRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating subscription:', error)
      throw error
    }
  },

  // Update subscription by userId (for when we don't have the subscription ID)
  async updateSubscriptionByUserId(userId: string, updates: Partial<Subscription>): Promise<void> {
    try {
      const subscriptionsQuery = query(
        collection(firestore, COLLECTIONS.SUBSCRIPTIONS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      )
      
      const querySnapshot = await getDocs(subscriptionsQuery)
      if (querySnapshot.empty) {
        throw new Error('No subscription found for user')
      }
      
      const subscriptionDoc = querySnapshot.docs[0]
      const currentData = subscriptionDoc.data() as Subscription
      
      await updateDoc(subscriptionDoc.ref, {
        ...updates,
        updatedAt: serverTimestamp()
      })
      
      // Track subscription status changes
      if (updates.status && updates.status !== currentData.status) {
        if (updates.status === 'active') {
          await AnalyticsService.logSubscriptionCreated(currentData.planType, currentData.pricePerMonth || 0)
        } else if (updates.status === 'cancelled') {
          await AnalyticsService.logEvent('subscription_cancelled', {
            plan_type: currentData.planType,
            amount: currentData.pricePerMonth || 0,
            currency: 'ZAR'
          })
        }
      }
    } catch (error) {
      console.error('Error updating subscription by userId:', error)
      throw error
    }
  },

  // Check payment failure status
  async checkPaymentFailureStatus(): Promise<{
    hasPaymentIssues: boolean
    failureCount?: number
    lastFailureReason?: string
    lastFailureDate?: any
    subscriptionCode?: string
  }> {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functions = getFunctions()

      const getPaymentFailureStatusFunction = httpsCallable(functions, 'getPaymentFailureStatus')
      const result = await getPaymentFailureStatusFunction({})

      return result.data as any
    } catch (error) {
      console.error('Error checking payment failure status:', error)
      return { hasPaymentIssues: false }
    }
  },

  // Update payment method
  async updatePaymentMethod(subscriptionCode: string, newAuthorizationCode: string): Promise<void> {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functions = getFunctions()

      const updatePaymentMethodFunction = httpsCallable(functions, 'updatePaymentMethod')
      await updatePaymentMethodFunction({
        subscriptionCode,
        newAuthorizationCode
      })
    } catch (error) {
      console.error('Error updating payment method:', error)
      throw error
    }
  },

  // Get user notifications
  async getUserNotifications(): Promise<any[]> {
    try {
      const { getAuth } = await import('firebase/auth')
      const auth = getAuth()
      if (!auth.currentUser) {
        throw new Error('User not authenticated')
      }

      const notificationsQuery = query(
        collection(firestore, 'notifications'),
        where('userId', '==', auth.currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(10)
      )
      const querySnapshot = await getDocs(notificationsQuery)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (error) {
      console.error('Error getting user notifications:', error)
      return []
    }
  },

  // Mark notification as read
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(firestore, 'notifications', notificationId)
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw error
    }
  },

  // Create trial subscription for new user
  async createTrialSubscription(userId: string, planType: 'individual' | 'club', courts?: number): Promise<string> {
    try {
      // Get admin settings for trial duration
      const adminSettings = await adminService.getAdminSettings()
      const trialDurationDays = adminSettings?.trialDurationDays ?? 60
      
      // Calculate trial end date
      const now = new Date()
      const trialEndsAt = new Date(now.getTime() + (trialDurationDays * 24 * 60 * 60 * 1000))
      
      // Get pricing plan for the selected type
      const plans = await pricingService.getPricingPlans()
      const selectedPlan = plans.find((plan: SubscriptionPlan) => plan.type === planType)
      
      if (!selectedPlan) {
        throw new Error(`No pricing plan found for type: ${planType}`)
      }

      const subscriptionData: Omit<Subscription, 'createdAt' | 'updatedAt'> = {
        userId,
        planType,
        status: 'trial',
        trialEndsAt: trialEndsAt as any,
        currentPeriodStart: now as any,
        currentPeriodEnd: trialEndsAt as any,
        pricePerMonth: selectedPlan.price,
        courts: courts || (planType === 'club' ? 1 : undefined),
        paystackPlanCode: selectedPlan.paystackPlanCode,
        isDiscountVersion: selectedPlan.isDiscountVersion || false,
        autoRenew: false
      }

      const subscriptionRef = await addDoc(collection(firestore, COLLECTIONS.SUBSCRIPTIONS), subscriptionData)
      
      // Track trial started event
      await AnalyticsService.logTrialStarted(planType)
      
      return subscriptionRef.id
    } catch (error) {
      console.error('Error creating trial subscription:', error)
      throw error
    }
  },

  // Get all subscriptions (for admin)
  async getAllSubscriptions(): Promise<Subscription[]> {
    try {
      const querySnapshot = await getDocs(collection(firestore, COLLECTIONS.SUBSCRIPTIONS))
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as Subscription[]
    } catch (error) {
      console.error('Error getting all subscriptions:', error)
      throw error
    }
  },

  // Check Paystack subscription status for user
  async checkPaystackSubscription(userId: string, subscriptionCode?: string): Promise<{ hasActiveSubscription: boolean; subscriptionData?: any }> {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functions = getFunctions()
      
      const getPaystackSubscriptionFunction = httpsCallable(functions, 'getPaystackSubscription')
      const result = await getPaystackSubscriptionFunction({ 
        userId,
        subscriptionCode 
      })
      

      
      if (result.data) {
        const subscriptionData = result.data as any
        // Check if subscription is active (not expired, cancelled, etc.)
        const hasActiveSubscription = subscriptionData && 
          (subscriptionData.status === 'active' || 
           subscriptionData.status === 'trial' ||
           (subscriptionData.next_payment_date && new Date(subscriptionData.next_payment_date) > new Date()))
        
        return {
          hasActiveSubscription,
          subscriptionData
        }
      }
      
      return { hasActiveSubscription: false }
    } catch (error) {
      console.error('Error checking Paystack subscription:', error)
      // If we can't check Paystack, assume no active subscription
      return { hasActiveSubscription: false }
    }
  }
}

// Payment Management
export const paymentService = {
  // Create payment record
  async createPayment(paymentData: Omit<Payment, 'createdAt'>): Promise<string> {
    try {
      const paymentRef = await addDoc(collection(firestore, COLLECTIONS.PAYMENTS), {
        ...paymentData,
        createdAt: serverTimestamp()
      })
      return paymentRef.id
    } catch (error) {
      console.error('Error creating payment:', error)
      throw error
    }
  },

  // Update payment status
  async updatePaymentStatus(paymentId: string, status: Payment['status'], paidAt?: Date): Promise<void> {
    try {
      const paymentRef = doc(firestore, COLLECTIONS.PAYMENTS, paymentId)
      await updateDoc(paymentRef, {
        status,
        paidAt: paidAt ? Timestamp.fromDate(paidAt) : null,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating payment status:', error)
      throw error
    }
  },

  // Get user payments
  async getUserPayments(userId: string): Promise<Payment[]> {
    try {
      const paymentsQuery = query(
        collection(firestore, COLLECTIONS.PAYMENTS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      )
      
      const querySnapshot = await getDocs(paymentsQuery)
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as Payment[]
    } catch (error) {
      console.error('Error getting user payments:', error)
      throw error
    }
  }
}

// Pricing Plans Management
export const pricingService = {
  // Get all pricing plans
  async getPricingPlans(): Promise<SubscriptionPlan[]> {
    try {
      // Try with ordering first
      try {
        const plansQuery = query(
          collection(firestore, COLLECTIONS.PRICING_PLANS),
          orderBy('createdAt', 'asc')
        )
        const querySnapshot = await getDocs(plansQuery)
              const plans = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as SubscriptionPlan[]
      
      return plans
      } catch (orderError) {
        console.warn('Failed to order by createdAt, falling back to unordered query:', orderError)
        
        // Fallback to unordered query
        const querySnapshot = await getDocs(collection(firestore, COLLECTIONS.PRICING_PLANS))
        const plans = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as unknown as SubscriptionPlan[]
        
        return plans
      }
    } catch (error) {
      console.error('Error getting pricing plans:', error)
      throw error
    }
  },

  // Get Paystack plan code for a specific plan type and courts
  async getPaystackPlanCode(planType: 'individual' | 'club', courts?: number, promoCode?: string): Promise<string> {
    try {
      const plans = await this.getPricingPlans()
      
      // Find the matching plan (same logic as deployed version)
      const matchingPlan = plans.find(plan => {
        if (plan.type !== planType) return false
        
        if (planType === 'individual') {
          return !plan.courts // Individual plans don't have courts
        } else {
          return plan.courts === courts // Club plans must match court count
        }
      })
      
      if (!matchingPlan) {
        console.error('No matching plan found for:', { planType, courts })
        // Fallback to hardcoded values
        if (planType === 'individual') {
          return promoCode ? 'PLN_gfr3ndqefs0yzfy' : 'PLN_i9fsaufn2zwtwok'
        } else {
          // Club plans fallback
          if (promoCode) {
            switch (courts) {
              case 1: return 'PLN_8s7lulkc8xpkoeq'
              case 2: return 'PLN_936j2tt0xia6c3x'
              case 3: return 'PLN_9smz3zz6se4lfti'
              case 4: return 'PLN_uv5k98otiwag11w'
              case 5: return 'PLN_4z17uu3cahx1bky'
              case 6: return 'PLN_quiirydpmwgx6q8'
              default: return 'PLN_8s7lulkc8xpkoeq'
            }
          } else {
            switch (courts) {
              case 1: return 'PLN_cgtjkxf8wkauc40'
              case 2: return 'PLN_pbnxijwibatzao3'
              case 3: return 'PLN_piyancmoj5pszaz'
              case 4: return 'PLN_p7t2lf5lmqjq2uo'
              case 5: return 'PLN_vxfm3nduu2fnbyx'
              case 6: return 'PLN_aflfmhpfmrz67z2'
              default: return 'PLN_cgtjkxf8wkauc40'
            }
          }
        }
      }
      
      // If promo code is applied, find the discount version (same logic as deployed version)
      if (promoCode && matchingPlan.id) {
        const discountPlan = plans.find(plan => 
          plan.originalPlanId === matchingPlan.id && plan.isDiscountVersion === true
        )
        
        if (discountPlan?.paystackPlanCode) {
          console.log('Using discount plan:', discountPlan.name, 'with code:', discountPlan.paystackPlanCode)
          return discountPlan.paystackPlanCode
        }
      }
      
      // Return the original plan's Paystack code
      if (matchingPlan?.paystackPlanCode) {
        return matchingPlan.paystackPlanCode
      }
      
      // Final fallback
      console.error('No Paystack plan code found for plan:', matchingPlan)
      return 'PLN_i9fsaufn2zwtwok'
    } catch (error) {
      console.error('Error getting Paystack plan code:', error)
      // Fallback to individual plan code
      return promoCode ? 'PLN_gfr3ndqefs0yzfy' : 'PLN_i9fsaufn2zwtwok'
    }
  },

  // Create pricing plan
  async createPricingPlan(planData: Omit<SubscriptionPlan, 'id'>): Promise<string> {
    try {
      const planRef = await addDoc(collection(firestore, COLLECTIONS.PRICING_PLANS), {
        ...planData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return planRef.id
    } catch (error) {
      console.error('Error creating pricing plan:', error)
      throw error
    }
  },

  // Update pricing plan
  async updatePricingPlan(planId: string, updates: Partial<SubscriptionPlan>): Promise<void> {
    try {
      const planRef = doc(firestore, COLLECTIONS.PRICING_PLANS, planId)
      await updateDoc(planRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating pricing plan:', error)
      throw error
    }
  },

  // Delete pricing plan
  async deletePricingPlan(planId: string): Promise<void> {
    try {
      await deleteDoc(doc(firestore, COLLECTIONS.PRICING_PLANS, planId))
    } catch (error) {
      console.error('Error deleting pricing plan:', error)
      throw error
    }
  }
}

// Promo Code Management
export const promoCodeService = {
  // Get all promo codes
  async getPromoCodes(): Promise<PromoCode[]> {
    try {
      const querySnapshot = await getDocs(collection(firestore, COLLECTIONS.PROMO_CODES))
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as unknown as PromoCode[]
    } catch (error) {
      console.error('Error getting promo codes:', error)
      throw error
    }
  },

  // Get promo code by code
  async getPromoCodeByCode(code: string): Promise<PromoCode | null> {
    try {
      // First try with the isActive filter
      const promoQuery = query(
        collection(firestore, COLLECTIONS.PROMO_CODES),
        where('code', '==', code.toUpperCase()),
        where('isActive', '==', true)
      )
      
      const querySnapshot = await getDocs(promoQuery)
      
      if (!querySnapshot.empty) {
        const promoData = querySnapshot.docs[0].data()
        return {
          id: querySnapshot.docs[0].id,
          ...promoData
        } as unknown as PromoCode
      }
      
      // If no results with isActive filter, try without it (in case isActive field is missing)
      const fallbackQuery = query(
        collection(firestore, COLLECTIONS.PROMO_CODES),
        where('code', '==', code.toUpperCase())
      )
      
      const fallbackSnapshot = await getDocs(fallbackQuery)
      
      if (!fallbackSnapshot.empty) {
        const promoData = fallbackSnapshot.docs[0].data()
        
        // Check if the promo code is active (default to true if field is missing)
        const isActive = promoData.isActive !== false // true if missing or explicitly true
        
        if (isActive) {
          return {
            id: fallbackSnapshot.docs[0].id,
            ...promoData
          } as unknown as PromoCode
        }
      }
      
      return null
    } catch (error) {
      console.error('Error getting promo code by code:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code,
        details: (error as any)?.details
      })
      throw error
    }
  },

  // Create promo code
  async createPromoCode(promoData: Omit<PromoCode, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const promoRef = await addDoc(collection(firestore, COLLECTIONS.PROMO_CODES), {
        ...promoData,
        code: promoData.code.toUpperCase(),
        usedCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return promoRef.id
    } catch (error) {
      console.error('Error creating promo code:', error)
      throw error
    }
  },

  // Update promo code
  async updatePromoCode(promoId: string, updates: Partial<PromoCode>): Promise<void> {
    try {
      const promoRef = doc(firestore, COLLECTIONS.PROMO_CODES, promoId)
      await updateDoc(promoRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating promo code:', error)
      throw error
    }
  },

  // Delete promo code
  async deletePromoCode(promoId: string): Promise<void> {
    try {
      await deleteDoc(doc(firestore, COLLECTIONS.PROMO_CODES, promoId))
    } catch (error) {
      console.error('Error deleting promo code:', error)
      throw error
    }
  },

  // Increment usage count
  async incrementPromoCodeUsage(promoId: string): Promise<void> {
    try {
      const promoRef = doc(firestore, COLLECTIONS.PROMO_CODES, promoId)
      await updateDoc(promoRef, {
        usedCount: increment(1),
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error incrementing promo code usage:', error)
      throw error
    }
  },


}

// Initialize default data
export const initializeFirestoreData = async (): Promise<void> => {
  try {
    // Check if admin settings already exist
    const existingSettings = await adminService.getAdminSettings()
    
    if (!existingSettings) {
      try {
        await adminService.initializeAdminSettings()
      } catch (error) {
        // This is expected in production mode without admin auth
      }
    }
    
    // Check if pricing plans exist
    const existingPlans = await pricingService.getPricingPlans()
    
    if (existingPlans.length === 0) {
      try {
        // Initialize default pricing plans
        const defaultPlans: Omit<SubscriptionPlan, 'id'>[] = [
          {
            type: 'individual',
            name: 'Individual Plan',
            description: 'Perfect for individual players',
            price: 4900, // R49.00 in cents
            paystackPlanCode: 'PLN_i9fsaufn2zwtwok', // Individual plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              '2 connected devices',
              'QR code sharing',
              'Basic statistics',
              'Email support'
            ],
            limitations: []
          },
          {
            type: 'individual',
            name: 'Individual Plan (Discounted)',
            description: 'Perfect for individual players with promo code',
            price: 4165, // R41.65 in cents (15% discount)
            paystackPlanCode: 'PLN_gfr3ndqefs0yzfy', // Individual discounted plan code
            originalPlanId: 'individual_plan_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              '2 connected devices',
              'QR code sharing',
              'Basic statistics',
              'Email support'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 1 Court',
            description: 'Tiered pricing for clubs and facilities',
            price: 39000, // R390.00 per court
            courts: 1,
            paystackPlanCode: 'PLN_cgtjkxf8wkauc40', // Club 1 court plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 1 Court (Discounted)',
            description: 'Tiered pricing for clubs and facilities with promo code',
            price: 33150, // R331.50 per court (15% discount)
            courts: 1,
            paystackPlanCode: 'PLN_8s7lulkc8xpkoeq', // Club 1 court discounted plan code
            originalPlanId: 'club_1_court_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 2 Courts',
            description: 'Tiered pricing for clubs and facilities',
            price: 37000, // R370.00 per court
            courts: 2,
            paystackPlanCode: 'PLN_pbnxijwibatzao3', // Club 2 courts plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 2 Courts (Discounted)',
            description: 'Tiered pricing for clubs and facilities with promo code',
            price: 31450, // R314.50 per court (15% discount)
            courts: 2,
            paystackPlanCode: 'PLN_936j2tt0xia6c3x', // Club 2 courts discounted plan code
            originalPlanId: 'club_2_courts_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 3 Courts',
            description: 'Tiered pricing for clubs and facilities',
            price: 35000, // R350.00 per court
            courts: 3,
            paystackPlanCode: 'PLN_piyancmoj5pszaz', // Club 3 courts plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 3 Courts (Discounted)',
            description: 'Tiered pricing for clubs and facilities with promo code',
            price: 29750, // R297.50 per court (15% discount)
            courts: 3,
            paystackPlanCode: 'PLN_9smz3zz6se4lfti', // Club 3 courts discounted plan code
            originalPlanId: 'club_3_courts_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 4 Courts',
            description: 'Tiered pricing for clubs and facilities',
            price: 33000, // R330.00 per court
            courts: 4,
            paystackPlanCode: 'PLN_p7t2lf5lmqjq2uo', // Club 4 courts plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 4 Courts (Discounted)',
            description: 'Tiered pricing for clubs and facilities with promo code',
            price: 28050, // R280.50 per court (15% discount)
            courts: 4,
            paystackPlanCode: 'PLN_uv5k98otiwag11w', // Club 4 courts discounted plan code
            originalPlanId: 'club_4_courts_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 5 Courts',
            description: 'Tiered pricing for clubs and facilities',
            price: 31000, // R310.00 per court
            courts: 5,
            paystackPlanCode: 'PLN_vxfm3nduu2fnbyx', // Club 5 courts plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 5 Courts (Discounted)',
            description: 'Tiered pricing for clubs and facilities with promo code',
            price: 26350, // R263.50 per court (15% discount)
            courts: 5,
            paystackPlanCode: 'PLN_4z17uu3cahx1bky', // Club 5 courts discounted plan code
            originalPlanId: 'club_5_courts_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 6 Courts',
            description: 'Tiered pricing for clubs and facilities',
            price: 29000, // R290.00 per court
            courts: 6,
            paystackPlanCode: 'PLN_aflfmhpfmrz67z2', // Club 6 courts plan code
            isDiscountVersion: false,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          },
          {
            type: 'club',
            name: 'Club Plan - 6 Courts (Discounted)',
            description: 'Tiered pricing for clubs and facilities with promo code',
            price: 24650, // R246.50 per court (15% discount)
            courts: 6,
            paystackPlanCode: 'PLN_quiirydpmwgx6q8', // Club 6 courts discounted plan code
            originalPlanId: 'club_6_courts_id', // Will be set after plan creation
            isDiscountVersion: true,
            features: [
              'Unlimited matches',
              'Unlimited connected devices',
              'Permanent QR code display',
              'Advanced statistics',
              'Priority support',
              'Club management tools'
            ],
            limitations: []
          }
        ]
        
        // Create plans in order: original plans first, then discount plans
        const originalPlans = defaultPlans.filter(plan => !plan.isDiscountVersion)
        const discountPlans = defaultPlans.filter(plan => plan.isDiscountVersion)
        
        // Create original plans first
        const createdPlanIds: { [key: string]: string } = {}
        
        for (const plan of originalPlans) {
          const planId = await pricingService.createPricingPlan({
            ...plan,
            createdAt: serverTimestamp() as any,
            updatedAt: serverTimestamp() as any
          })
          
          // Store the plan ID for linking discount plans
          const planKey = plan.type === 'individual' ? 'individual' : `club_${plan.courts}_courts`
          createdPlanIds[planKey] = planId
        }
        
        // Create discount plans with proper originalPlanId links
        for (const plan of discountPlans) {
          const planKey = plan.type === 'individual' ? 'individual' : `club_${plan.courts}_courts`
          const originalPlanId = createdPlanIds[planKey]
          
          if (originalPlanId) {
            await pricingService.createPricingPlan({
              ...plan,
              originalPlanId: originalPlanId,
              createdAt: serverTimestamp() as any,
              updatedAt: serverTimestamp() as any
            })
          }
        }
      } catch (error) {
        // This is expected in production mode without admin auth
      }
    }
  } catch (error) {
    // Don't throw error, just log it
  }
}

// Subscription cancellation service
export const subscriptionCancellationService = {
  async cancelSubscription(subscriptionCode: string): Promise<void> {
    try {
      const { httpsCallable } = await import('firebase/functions')
      const { getFunctions } = await import('firebase/functions')
      const { getApp } = await import('firebase/app')
      const { app } = await import('../config/firebase')
      
      const functions = getFunctions(app)
      const disableSubscription = httpsCallable(functions, 'disableSubscription')
      
      await disableSubscription({ subscriptionCode })
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      throw error
    }
  },

  async cancelAutoRenewal(subscriptionCode: string): Promise<void> {
    try {
      const { httpsCallable } = await import('firebase/functions')
      const { getFunctions } = await import('firebase/functions')
      const { getApp } = await import('firebase/app')
      const { app } = await import('../config/firebase')
      
      const functions = getFunctions(app)
      const cancelAutoRenewal = httpsCallable(functions, 'cancelAutoRenewal')
      
      await cancelAutoRenewal({ subscriptionCode })
    } catch (error) {
      console.error('Error cancelling auto-renewal:', error)
      throw error
    }
  }
} 